import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { getVault, removeVaultPasskeys } from '../transport/relay'
import { VaultChangePassword, VaultFaceIdSetup, VaultRecover, VaultReset, VaultSetup } from './VaultForms'
import { faceIdAvailable } from './passkey'

/**
 * THE VAULT PASSWORD, FROM THE ACCOUNT CARD — owner, 2026-09-30: 主選單上面, 使用者 Account
 * 的地方可以新增一個按鈕, 讓使用者可以來設定初始的 Vault Password, 或者是更改 Vault Password.
 *
 * No vault: set one up (the first backup's setup, recovery code shown once). A vault:
 * Change password (the current one re-locks the same key — backups kept), the recovery
 * code for a forgotten one (backups kept), and Reset — the only door that loses backups,
 * and its own screen says so in red before deleting them all (his 09-29 rule).
 */
type View = 'closed' | 'menu' | 'setup' | 'change' | 'recover' | 'reset' | 'faceid' | 'faceid-on'

export function VaultPasswordRow() {
  const qc = useQueryClient()
  const vault = useQuery({ queryKey: ['backup-vault'], queryFn: getVault, staleTime: 30_000 })
  const [view, setView] = useState<View>('closed')
  const [said, setSaid] = useState<string | null>(null)
  const has = !!vault.data?.vault
  const faceIdOn = !!vault.data?.vault?.pk?.length
  // Face ID / Touch ID (owner, 2026-10-01): offered only where this device has it.
  const [canFaceId, setCanFaceId] = useState(false)
  useEffect(() => {
    void faceIdAvailable().then(setCanFaceId)
  }, [])
  const [offBusy, setOffBusy] = useState(false)
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['backup-vault'] })
    void qc.invalidateQueries({ queryKey: ['backups'] })
  }
  const done = (msg: string) => {
    setSaid(msg)
    setView('closed')
    refresh()
  }

  if (vault.isError) return null

  return (
    <div className="vpw">
      {view === 'closed' && (
        <button type="button" className="vpw-row" onClick={() => setView(has ? 'menu' : 'setup')} disabled={vault.isLoading}>
          <Icon name="lock" size={18} />
          <span className="vpw-label">Vault password</span>
          <span className="vpw-val">{vault.isLoading ? '' : has ? 'Change' : 'Set up'}</span>
          <Icon name="chevron_right" size={18} />
        </button>
      )}
      {view === 'closed' && said && <p className="sm-hint vpw-said">{said}</p>}

      {view === 'menu' && (
        <div className="vpw-menu">
          <button type="button" className="ac-back" aria-label="Back" onClick={() => setView('closed')}>
            <Icon name="chevron_left" size={18} />
            Vault password
          </button>
          <button type="button" className="vpw-row" onClick={() => setView('change')}>
            <Icon name="edit" size={18} />
            <span className="vpw-label">Change password</span>
            <Icon name="chevron_right" size={18} />
          </button>
          {canFaceId && (
            <button type="button" className="vpw-row" onClick={() => setView(faceIdOn ? 'faceid-on' : 'faceid')}>
              <Icon name="fingerprint" size={18} />
              <span className="vpw-label">Face ID / Touch ID</span>
              <span className="vpw-val">{faceIdOn ? 'On' : 'Set up'}</span>
              <Icon name="chevron_right" size={18} />
            </button>
          )}
          <button type="button" className="vpw-row" onClick={() => setView('recover')}>
            <Icon name="history" size={18} />
            <span className="vpw-label">Forgot it — use the recovery code</span>
            <Icon name="chevron_right" size={18} />
          </button>
          <button type="button" className="vpw-row vpw-danger" onClick={() => setView('reset')}>
            <Icon name="delete" size={18} />
            <span className="vpw-label">Reset vault — deletes every backup</span>
            <Icon name="chevron_right" size={18} />
          </button>
        </div>
      )}

      {view === 'setup' && (
        <VaultSetup
          machine="your machines"
          continueLabel="Done"
          onReady={() => done('Your vault is set up. Keep the recovery code safe.')}
          onBack={() => setView('closed')}
          onExists={() => {
            refresh()
            setView('menu')
          }}
        />
      )}
      {view === 'change' && vault.data?.vault && (
        <VaultChangePassword
          vault={vault.data.vault}
          onBack={() => setView('menu')}
          onDone={() => done('Password changed. Your backups are kept.')}
        />
      )}
      {view === 'faceid' && vault.data?.vault && (
        <VaultFaceIdSetup
          vault={vault.data.vault}
          onBack={() => setView('menu')}
          onDone={() => done('Face ID / Touch ID now opens your vault.')}
        />
      )}
      {view === 'faceid-on' && (
        <div className="vpw-menu">
          <button type="button" className="ac-back" aria-label="Back" onClick={() => setView('menu')}>
            <Icon name="chevron_left" size={18} />
            Face ID / Touch ID
          </button>
          <button type="button" className="vpw-row" onClick={() => setView('faceid')}>
            <Icon name="fingerprint" size={18} />
            <span className="vpw-label">Set up again on this device</span>
            <Icon name="chevron_right" size={18} />
          </button>
          <button
            type="button"
            className="vpw-row vpw-danger"
            disabled={offBusy}
            onClick={() => {
              setOffBusy(true)
              void removeVaultPasskeys()
                .then(() => done('Face ID / Touch ID is off. Your password still opens your vault.'))
                .catch((e: unknown) => setSaid(e instanceof Error ? e.message : String(e)))
                .finally(() => setOffBusy(false))
            }}
          >
            <Icon name="close" size={18} />
            <span className="vpw-label">{offBusy ? 'Turning off…' : 'Turn off'}</span>
          </button>
        </div>
      )}
      {view === 'recover' && vault.data?.vault && (
        <VaultRecover
          vault={vault.data.vault}
          onBack={() => setView('menu')}
          onLostBoth={() => setView('reset')}
          onDone={() => done('Password changed. Your backups are kept.')}
        />
      )}
      {view === 'reset' && (
        <VaultReset
          onBack={() => setView('menu')}
          onDone={(n) => done(`Vault reset — ${n} backup${n === 1 ? '' : 's'} deleted. Set a new password with your next backup, or here.`)}
        />
      )}
    </div>
  )
}
